import React from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Bot, Zap, Star, DollarSign, Info } from "lucide-react";

interface ModelSelectorProps {
  taskType: "blueprint" | "chapter_outline" | "chapter_text" | "story_enhancement";
  onModelSelect: (model: LlmModel) => void;
  children: React.ReactNode;
  baseCost: number;
}

interface LlmModel {
  id: number;
  provider: string;
  modelName: string;
  apiName: string;
  contextWindow: string;
  inputCostPer1mTok: number;
  outputCostPer1mTok: number;
  capabilityIndex: number;
  creditMultiplier: number;
  minPlanRequired: string;
  isActive: boolean;
  keyNotes: string;
  createdAt: string;
  updatedAt: string;
}

const TASK_DESCRIPTIONS = {
  blueprint: {
    title: "Story Blueprint Generation",
    description: "Creates comprehensive story structure, character development, and plot outline",
    recommendedCategory: "premium",
    recommendation: "High-end models are recommended for complex story planning and creative development"
  },
  chapter_outline: {
    title: "Chapter Outline Creation", 
    description: "Generates detailed chapter summaries and story progression",
    recommendedCategory: "premium",
    recommendation: "High-end models are recommended for narrative consistency and plot development"
  },
  chapter_text: {
    title: "Chapter Text Generation",
    description: "Writes actual chapter content with dialogue and descriptions",
    recommendedCategory: "standard",
    recommendation: "Medium models are sufficient for text generation while maintaining quality"
  },
  story_enhancement: {
    title: "Story Enhancement",
    description: "Improves existing content, dialogue, and descriptions",
    recommendedCategory: "standard", 
    recommendation: "Medium models provide good quality improvements at reasonable cost"
  }
};

const PROVIDER_ICONS: Record<string, React.ElementType> = {
  "OpenAI": Star,
  "Google": Zap,
  "DeepSeek": DollarSign,
  "Anthropic": Bot
};

export function ModelSelector({ taskType, onModelSelect, children, baseCost }: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<LlmModel | null>(null);

  const { data: userData } = useQuery<{ user: any }>({
    queryKey: ["/api/auth/me"],
  });

  const { data: models } = useQuery<LlmModel[]>({
    queryKey: ["/api/llm-models"],
    enabled: open,
  });

  const taskInfo = TASK_DESCRIPTIONS[taskType];
  const userPlan = userData?.user?.planCode || "FREE";

  // Filter models based on user's plan and active status
  const availableModels = models?.filter(model => {
    if (!model.isActive) return false;

    // Add this check
    if (!userPlan) return false;

    const planHierarchy = ["FREE", "PRO", "ENTERPRISE"];
    const userPlanIndex = planHierarchy.indexOf(userPlan);
    const requiredPlanIndex = planHierarchy.indexOf(model.minPlanRequired);

    return userPlanIndex >= requiredPlanIndex;
  }) || [];

  // Group models by provider
  const modelsByProvider = availableModels.reduce((acc, model) => {
    if (!acc[model.provider]) acc[model.provider] = [];
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<string, LlmModel[]>);

  const handleModelSelect = (model: LlmModel) => {
    setSelectedModel(model);
    onModelSelect(model);
    setOpen(false);
  };

  const calculateCost = (multiplier: number) => {
    return Math.round(baseCost * multiplier);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Choose AI Model for {taskInfo.title}
          </DialogTitle>
          <DialogDescription>
            {taskInfo.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Task Information */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-600" />
                <CardTitle className="text-sm text-blue-800">Recommendation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-blue-700">{taskInfo.recommendation}</p>
              <div className="mt-2 flex items-center gap-2 text-xs text-blue-600">
                <span>Base cost: {baseCost.toLocaleString()} credits</span>
                <span>•</span>
                <span>Final cost varies by model multiplier</span>
              </div>
            </CardContent>
          </Card>

          {/* Model Providers */}
          {Object.entries(modelsByProvider).map(([provider, providerModels]) => {
            if (!Array.isArray(providerModels) || providerModels.length === 0) return null;

            return (
              <div key={provider}>
                <div className="flex items-center gap-2 mb-3">
                  {(() => {
                    const IconComponent = PROVIDER_ICONS[provider] || Bot;
                    return <IconComponent className="h-4 w-4" />;
                  })()}
                  <h3 className="font-semibold">{provider}</h3>
                  <Badge variant="outline" className="text-xs">
                    {providerModels.length} model{providerModels.length > 1 ? 's' : ''}
                  </Badge>
                </div>
                
                <div className="grid gap-3">
                  {(providerModels as LlmModel[]).map(model => (
                    <Card 
                      key={model.id}
                      className="cursor-pointer transition-all hover:shadow-md hover:ring-2 hover:ring-blue-200"
                      onClick={() => handleModelSelect(model)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div>
                                <h4 className="font-medium">{model.modelName}</h4>
                                <p className="text-sm text-muted-foreground">
                                  {model.keyNotes}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {model.contextWindow} context
                                </Badge>
                                <Badge 
                                  variant={model.minPlanRequired === "FREE" ? "secondary" : "default"}
                                  className="text-xs"
                                >
                                  {model.minPlanRequired}+
                                </Badge>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                              Capability: {model.capabilityIndex}/100 | 
                              Cost: ${model.inputCostPer1mTok}/${model.outputCostPer1mTok} per 1M tokens
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-semibold">
                              {calculateCost(model.creditMultiplier).toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              credits (×{model.creditMultiplier})
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {provider !== Object.keys(modelsByProvider)[Object.keys(modelsByProvider).length - 1] && (
                  <Separator className="mt-6" />
                )}
              </div>
            );
          })}

          {/* Plan Access Information */}
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  Current Plan: {userPlan}
                </span>
              </div>
              <p className="text-xs text-amber-700">
                Some models may require plan upgrades. 
                {userPlan === "FREE" && " Consider upgrading to access premium models."}
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}